import React, { useCallback, useContext, createContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  SidebarContainer,
  SidebarHeader,
  SidebarMenu,
  SidebarGroup,
  SidebarItem,
  SidebarLink,
  SidebarTitle,
  SidebarIcon,
  SidebarText,
} from "./Sidebar.styles";
import SidebarUserFooter from "./SidebarUserFooter";
import {
  courseImg,
  statsImg,
  studentIcon,
  userProfileIcon as UserProfileIcon,
  homeworkIcon,
  knowledgeBaseIcon,
  refIcon,
  paymentIcon,
  settingsIcon as SettingsIcon,
  helpIcon,
  docTempIcon,
  leadsIcon,
  calendarIcon,
} from "assets/icons";
import { useUser } from "../../context/UserContext";
import { useAuth } from "../../context/AuthContext";
import { fetchKnowledgeBaseStatus } from "../../services/knowledgeBaseService/knowledgeBaseService";
import { pxToRem } from "../../styles/utils";

const SidebarContext = createContext({ expanded: true });

const SidebarLabel = styled.span`
  display: inline-flex;
  align-items: baseline;
  min-width: 0;
`;

const SidebarBadge = styled.span`
  display: inline;
  margin-left: ${pxToRem(6)};
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.dangerMuted} !important;
  font-size: ${pxToRem(18)} !important;
  font-weight: 600 !important;
  line-height: inherit;
  white-space: nowrap;
`;

export const SidebarMenuProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [expanded] = useState(true);

  return (
    <SidebarContext.Provider value={{ expanded }}>
      {children}
    </SidebarContext.Provider>
  );
};

const SidebarItemComponent: React.FC<{
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  text: string;
  to: string;
  badge?: number;
}> = ({ icon, text, to, badge = 0 }) => {
  const { expanded } = useContext(SidebarContext);
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <SidebarItem>
      <Link to={to}>
        <SidebarLink isActive={isActive}>
          <SidebarIcon as={icon} aria-hidden="true" />
          {expanded ? (
            <SidebarLabel>
              <SidebarText>{text}</SidebarText>
              {badge > 0 ? (
                <SidebarBadge aria-label={`${badge} новых`}>
                  {badge > 9 ? "9+" : badge}
                </SidebarBadge>
              ) : null}
            </SidebarLabel>
          ) : null}
        </SidebarLink>
      </Link>
    </SidebarItem>
  );
};

const Sidebar: React.FC = () => {
  const { user } = useUser();
  const { logout } = useAuth();
  const location = useLocation();
  const [knowledgeUnread, setKnowledgeUnread] = useState(0);
  const userName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(" ")
    : "";

  const refreshKnowledgeBaseStatus = useCallback(() => {
    fetchKnowledgeBaseStatus()
      .then((status) => setKnowledgeUnread(status.unread_count || 0))
      .catch(() => setKnowledgeUnread(0));
  }, []);

  useEffect(() => {
    refreshKnowledgeBaseStatus();
    window.addEventListener("knowledge-base-status-changed", refreshKnowledgeBaseStatus);
    return () => window.removeEventListener("knowledge-base-status-changed", refreshKnowledgeBaseStatus);
  }, [location.pathname, refreshKnowledgeBaseStatus]);

  return (
    <SidebarContainer>
      <SidebarHeader>LISRENT</SidebarHeader>
      <SidebarMenu>
        <SidebarGroup>
          <SidebarTitle>Курсы и статистика</SidebarTitle>
          <SidebarItemComponent icon={courseImg} text="Мои курсы" to="/courses" />
          <SidebarItemComponent icon={knowledgeBaseIcon} text="База знаний" to="/knowledge-base" badge={knowledgeUnread} />
          <SidebarItemComponent icon={statsImg} text="Статистика" to="/statistics" />
        </SidebarGroup>
        <SidebarGroup>
          <SidebarTitle>Учеба</SidebarTitle>
          <SidebarItemComponent icon={leadsIcon} text="Лиды" to="/leads" />
          <SidebarItemComponent icon={studentIcon} text="Ученики" to="/students" />
          <SidebarItemComponent icon={calendarIcon} text="Календарь" to="/events" />
          <SidebarItemComponent
            icon={homeworkIcon}
            text="Домашние задания"
            to="/homework"
          />
        </SidebarGroup>
        <SidebarGroup>
          <SidebarTitle>Оплата и акции</SidebarTitle>
          <SidebarItemComponent icon={refIcon} text="Реферальная программа" to="/referals" />
          <SidebarItemComponent icon={paymentIcon} text="Оплата" to="/payment" />
        </SidebarGroup>
        <SidebarGroup>
          <SidebarTitle>Другое</SidebarTitle>
          <SidebarItemComponent icon={docTempIcon} text="Документы" to="/documents" />
          <SidebarItemComponent icon={helpIcon} text="Поддержка" to="/support-requests" />
        </SidebarGroup>
      </SidebarMenu>

      <SidebarUserFooter
        name={userName}
        email={user?.email || ""}
        isLoading={!user}
        menuLinks={[
          { to: "/profile", label: "Личный кабинет", icon: UserProfileIcon },
          { to: "/settings", label: "Настройки", icon: SettingsIcon },
        ]}
        onLogout={logout}
      />
    </SidebarContainer>
  );
};

export default Sidebar;
